import { nextTick } from 'vue'

/**
 * ============================================================
 * COMPOSABLE: useValidacao
 * ============================================================
 * Padrão de validação híbrida (backend → frontend) reutilizável
 * para QUALQUER formulário que possua accordions/frames colapsáveis
 * e/ou abas internas.
 *
 * O backend retorna uma resposta estruturada (ValidationErrorResponse):
 * {
 *   message: "...",
 *   errors: [
 *     { field: "idFilial", message: "Filial é obrigatória.", tab: "dadosBasicos" },
 *     ...
 *   ]
 * }
 *
 * Este composable processa esses erros e:
 *  1. Destaca cada campo inválido no formulário (formErrors.value[field])
 *  2. Abre o accordion/frame correspondente (accordionState[tab] = true)
 *  3. Ativa a aba correta quando o campo está em uma tab interna
 *     (activeTab.value = tab, abrindo antes o accordion contentor — padrão "abas")
 *  4. Foca no primeiro campo com erro
 *
 * Uso em qualquer formulário:
 *   const formErrors = ref({})
 *   const accordionState = reactive({ dadosBasicos: true, abas: true })
 *   const abaAtiva = ref('comercial')
 *
 *   const { processarErrosBackend } = useValidacao({
 *     formErrors,
 *     accordionState,
 *     activeTab: abaAtiva,
 *     abaContentor: 'abas',           // opcional (default) — accordion que contém as abas
 *     fieldTabMap: { idFilial: 'dadosBasicos', lstIncoterm: 'comercial' }
 *   })
 *
 *   try {
 *     await api.post(...)
 *   } catch (error) {
 *     const msg = processarErrosBackend(error.response?.data)
 *     emit('show-notification', { message: msg, type: 'error' })
 *   }
 *
 * 📌 REGRA DE NAVEGAÇÃO (importante para os formulários):
 *   - O `tab` recebido do backend pode ser:
 *     (a) uma chave de accordion/frame colapsável (ex: "dadosBasicos", "itens")
 *         → abre o frame e NÃO altera a aba interna;
 *     (b) um nome de aba interna (ex: "comercial", "pricing")
 *         → abre o accordion contentor (`abaContentor`, default "abas") e
 *           ativa a aba via `activeTab.value = tab`.
 *   - NUNCA usar nomes de abas como chaves do `accordionState` — isso criaria
 *     ambiguidade na navegação (o composable trataria a aba como accordion).
 */

/**
 * Abre o accordion/frame e ativa a tab correta para um dado campo.
 * @param {Object} params
 * @param {string} params.tab  Nome da tab/accordion (do backend ou do fieldTabMap)
 * @param {Object} params.accordionState  Estado reativo dos accordions (ex: reactive({...}))
 * @param {Object} params.activeTab Ref reativa da aba ativa (opcional)
 * @param {string} [params.abaContentor='abas'] Nome da chave do accordion que contém as abas internas
 */
function abrirAccordionETab ({ tab, accordionState, activeTab, abaContentor = 'abas' }) {
  if (!tab) return

  // 1. Se o nome corresponde a um accordion/frame colapsável, abre-o.
  //    (Neste caso NÃO mexe na aba interna — são operações ortogonais.)
  if (accordionState && tab in accordionState) {
    accordionState[tab] = true
    return
  }

  // 2. Caso contrário, `tab` é uma aba interna:
  //    2a. Garante que o accordion contentor das abas esteja aberto
  if (accordionState && abaContentor in accordionState) {
    accordionState[abaContentor] = true
  }

  //    2b. Ativa a aba
  if (activeTab && typeof activeTab.value === 'string') {
    activeTab.value = tab
  }
}

/**
 * Foca no elemento DOM de um campo após garantir que ele está visível.
 * @param {string} field Nome do campo (id do elemento)
 */
async function focarPrimeiroErro (field) {
  if (!field) return
  await nextTick()
  const el = document.getElementById(field)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.focus({ preventScroll: true })
  }
}

/**
 * Processa a resposta de validação do backend (ValidationErrorResponse).
 * Destaca campos, navega para abas/accordions e foca no primeiro erro.
 *
 * @param {Object} validationResponse  Resposta do backend ({ message, errors: [...] })
 * @param {Object} config Configuração do formulário:
 * @param {Object} config.formErrors Referência reativa de erros do formulário
 * @param {Object} [config.accordionState] Estado reativo dos accordions/frames
 * @param {Object} [config.activeTab] Ref reativa da aba ativa (abas internas)
 * @param {string} [config.abaContentor='abas'] Nome da chave do accordion que contém as abas
 * @param {Object} [config.fieldTabMap] Mapa field → tab (fallback quando o backend não envia tab)
 * @returns {string|null} Mensagem geral de erro (para emitir notificação) ou null
 */
export function useValidacao ({ formErrors, accordionState, activeTab, abaContentor = 'abas', fieldTabMap = {} }) {
  /**
   * Processa os erros estruturados do backend e aplica ao formulário.
   * @param {Object} data Response data do erro (error.response?.data)
   * @returns {string|null} Mensagem geral ou null se não houver erros estruturados
   */
  function processarErrosBackend (data = null) {
    // Limpa erros anteriores (sem reatribuir o objeto para preservar o Proxy)
    if (formErrors && typeof formErrors.value === 'object') {
      for (const key in formErrors.value) {
        delete formErrors.value[key]
      }
    }

    if (!data) return null

    // [DEBUG TEMPORÁRIO] — remover após diagnóstico
    console.log('[useValidacao] processarErrosBackend data:', JSON.stringify(data)?.slice(0, 500))
    console.log('[useValidacao] data.errors é array?', Array.isArray(data?.errors), '| keys:', data?.errors ? Object.keys(data.errors).slice(0, 5) : 'SEM ERRORS')

    // Suporta tanto o formato novo (errors: [{field, message, tab}])
    // quanto o formato antigo (errors: { campo: [mensagens] } do ModelState)
    let errosLista = null
    if (Array.isArray(data.errors)) {
      errosLista = data.errors
    } else if (data.errors && typeof data.errors === 'object') {
      errosLista = Object.entries(data.errors).map(([field, msgs]) => ({
        field,
        message: Array.isArray(msgs) ? msgs[0] : msgs,
        tab: null
      }))
    }

    if (!errosLista || errosLista.length === 0) {
      // Sem erros estruturados: apenas a mensagem genérica (fallback)
      return data.message || null
    }

    // 1. Destaca todos os campos inválidos e coleta a primeira tab relevante
    let primeiroErroSemInput = null
    errosLista.forEach((err, index) => {
      if (!err?.field) return
      const tab = err.tab || fieldTabMap[err.field]
      if (formErrors && typeof formErrors.value === 'object') {
        formErrors.value[err.field] = err.message || 'Campo inválido.'
      }
      // Abre accordion/tab do primeiro erro (os demais podem não estar visíveis)
      if (index === 0) {
        abrirAccordionETab({ tab, accordionState, activeTab, abaContentor })
      }
      // Identifica o primeiro erro que NÃO tem input DOM (grid/bloco)
      if (!primeiroErroSemInput && !document.getElementById(err.field)) {
        primeiroErroSemInput = err
      }
    })

    // 2. Foca no primeiro campo com erro que possua input DOM.
    //    Se o primeiro erro for de um grid/bloco sem elemento DOM (ex: resumoAprovacao),
    //    não tenta focar — apenas navega para a aba correta.
    if (errosLista[0]?.field && document.getElementById(errosLista[0].field)) {
      focarPrimeiroErro(errosLista[0].field)
    }

    // 3. Mensagem inteligente:
    //    - Se o primeiro erro da lista NÃO tem input DOM (grid/bloco), usa a
    //      mensagem específica dele (ex: "Para vendas de exportação, é obrigatório
    //      cadastrar as condições de pagamento detalhadas.")
    //    - Se o primeiro erro tem input DOM (campo simples), orientação genérica
    //      (o destaque is-invalid + invalid-feedback já informam o detalhe).
    //    - Se há erros MISTOS (campo + grid), prioriza a mensagem do grid/bloco
    //      (que não tem destaque visual próprio).
    const primeiroErro = errosLista[0]
    if (primeiroErroSemInput) {
      return primeiroErroSemInput.message || 'Preencha os campos obrigatórios destacados em vermelho.'
    }
    if (primeiroErro?.field && !document.getElementById(primeiroErro.field)) {
      return primeiroErro.message || 'Preencha os campos obrigatórios destacados em vermelho.'
    }
    return 'Preencha os campos obrigatórios destacados em vermelho.'
  }

  /**
   * Helper: processa o erro de uma chamada api e extrai a mensagem.
   * Retorna a mensagem geral do backend (para emitir notificação).
   * @param {Object} error Erro capturado no catch
   * @returns {string} Mensagem de erro
   */
  function capturarErroValidacao (error = null) {
    const data = error?.response?.data
    return processarErrosBackend(data) || 'Erro ao processar a solicitação.'
  }

  return {
    processarErrosBackend,
    capturarErroValidacao
  }
}

export default useValidacao
